import {AfterViewInit, Directive, ElementRef, OnDestroy, Renderer2} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {ResizeObserverService} from '../../components/resize-observer';

const TEXT_HIDING_CLASS = '-text-hiding';

@Directive({
    selector: '[textHiding]',
    standalone: false
})
export class TextHidingDirective implements AfterViewInit, OnDestroy {

    public isOverflowed: boolean = false;

    public readonly isOverflowed$ = new BehaviorSubject<boolean>(false);

    private readonly element: HTMLElement = this.elementRef.nativeElement;

    private containerWidth: number;

    private childWidth: number;

    private readonly unobserveContainer: () => void;

    private unobserveChild: () => void;

    constructor(
        private elementRef: ElementRef,
        private renderer: Renderer2,
        private resizeObserverService: ResizeObserverService
    ) {
        this.unobserveContainer = this.resizeObserverService.observeWithCallback(
            this.element,
            entry => this.onResize(entry, undefined)
        );
    }

    public ngOnDestroy(): void {
        if (this.unobserveChild) {
            this.unobserveChild();
        }

        if (this.unobserveContainer) {
            this.unobserveContainer();
        }
    }

    public ngAfterViewInit(): void {
        const childEl = this.element.children[0];

        if (!childEl) {
            console.warn('Missing child element in text-hiding directive.', this.element); // TODO
        } else {
            this.unobserveChild = this.resizeObserverService.observeWithCallback(
                childEl,
                entry => this.onResize(undefined, entry)
            );
        }
    }

    public onResize(
        containerEntry: ResizeObserverEntry | undefined,
        childEntry: ResizeObserverEntry | undefined
    ): void {
        const containerWidth = this.getContainerInnerWidth(containerEntry);
        const childWidth = this.getChildWidth(childEntry);

        if (containerEntry) {
            this.containerWidth = containerWidth;
        }

        if (childEntry) {
            this.childWidth = childWidth;
        }

        if (containerWidth !== undefined && childWidth !== undefined) {
            const isOverflowed = containerWidth < childWidth;

            if (isOverflowed !== this.isOverflowed) {
                this.toggleClass(TEXT_HIDING_CLASS, isOverflowed);
                this.isOverflowed = isOverflowed;
                this.isOverflowed$.next(isOverflowed);
            }
        }
    }

    private getContainerInnerWidth(containerEntry: ResizeObserverEntry | undefined): number {
        return containerEntry
            ? containerEntry.contentRect.width
            : this.containerWidth;
    }

    private getChildWidth(childEntry: ResizeObserverEntry | undefined): number {
        let width: number = this.childWidth;

        if (childEntry) {
            const size: ResizeObserverSize | undefined =
                childEntry.borderBoxSize && (
                    childEntry.borderBoxSize[0]

                    /**
                     * Some old variant, when it was in draft status?
                     * Ref.: https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserverEntry/borderBoxSize
                     */
                    || childEntry.borderBoxSize as undefined as ResizeObserverSize
                );

            width = size
                ? size.inlineSize

                /**
                 * Workaround: get inner content width.
                 * In order to get border-box width we may call
                 * element.getBoundingClientRect().width
                 * but this is slower, and usually isn't required in this app (no paddings in content).
                 */
                : childEntry.contentRect.width;
        }

        return width;
    }

    private toggleClass(className: string, state: boolean): void {
        const renderer = this.renderer;
        const element  = this.elementRef.nativeElement;

        if (state) {
            renderer.addClass(element, className);
        } else {
            renderer.removeClass(element, className);
        }
    }

}
